import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/lib/constants";
import { Eye, EyeOff, AlertCircle } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatErr(d) {
  if (d == null) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");           // inline error visible on the form
  const [alreadyExists, setAlreadyExists] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setAlreadyExists(false);

    // Client-side validation with CLEAR visible messages (no silent HTML5 tooltips)
    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    const phone = form.phone.trim();
    const password = form.password;

    if (!name) return setErr("Please enter your full name.");
    if (!email) return setErr("Please enter your email.");
    if (!EMAIL_RE.test(email)) return setErr("That email doesn't look right. Please double-check it.");
    if (!password || password.length < 6) return setErr("Password must be at least 6 characters.");

    setBusy(true);
    try {
      await register({ name, email, password, phone });
      toast.success("Account created!");
      nav("/portal-select");
    } catch (e) {
      const detail = formatErr(e?.response?.data?.detail);
      const status = e?.response?.status;
      // Friendlier UX for the most common failure
      if (/already registered/i.test(detail)) {
        setAlreadyExists(true);
        setErr("That email is already registered. Sign in instead, or use a different email.");
      } else if (!e?.response) {
        setErr("Can't reach the server. Check your internet connection and try again.");
      } else {
        setErr(detail || `Registration failed (HTTP ${status}). Please try again or contact support.`);
      }
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal-select";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#E32636] text-white p-12 relative overflow-hidden">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="logo" className="h-12 w-12 object-contain" />
          <div>
            <div className="font-heading font-black text-lg">CLEAVIE'S ENTERPRISE</div>
            <div className="text-xs tracking-[0.22em] text-[#FFCE00]">ANTIGUA & BARBUDA</div>
          </div>
        </Link>
        <div>
          <h2 className="font-heading font-black text-4xl">Join the island's most reliable service crew.</h2>
          <p className="mt-3 text-white/90 max-w-md">Create an account to book water, bins, diagnostics, trucking quotes and rentals — all in one place.</p>
        </div>
        <div className="text-xs text-white/70">© {new Date().getFullYear()} Cleavie's Enterprise</div>
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#FFCE00]/30 rounded-full blur-3xl" />
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <form onSubmit={submit} noValidate className="w-full max-w-sm space-y-5">
          <div className="lg:hidden flex items-center gap-3 mb-4">
            <img src={LOGO_URL} alt="logo" className="h-10 w-10 object-contain" />
            <div className="font-heading font-black text-slate-900">CLEAVIE'S ENTERPRISE</div>
          </div>
          <div>
            <div className="tag-overline">Create account</div>
            <h1 className="font-heading font-black text-3xl text-slate-900 mt-1">Let's get you set up</h1>
          </div>

          <Button type="button" onClick={googleLogin} variant="outline" data-testid="google-register-btn"
                  className="w-full h-11 border-2 border-slate-200 hover:border-slate-400 font-bold">
            <span className="mr-2">G</span> Continue with Google
          </Button>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <div className="h-px bg-slate-200 flex-1" /> OR <div className="h-px bg-slate-200 flex-1" />
          </div>

          {/* Inline error — always visible, impossible to miss */}
          {err && (
            <div data-testid="reg-error" className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="flex-1">
                {err}
                {alreadyExists && (
                  <div className="mt-1">
                    <Link to="/login" className="font-bold underline" data-testid="reg-goto-login">Sign in instead →</Link>
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <Label className="text-sm font-bold text-slate-900">Full name</Label>
            <Input data-testid="reg-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label className="text-sm font-bold text-slate-900">Email</Label>
            <Input type="email" inputMode="email" autoComplete="email" data-testid="reg-email"
                   value={form.email}
                   onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label className="text-sm font-bold text-slate-900">Phone <span className="font-normal text-slate-500">(optional)</span></Label>
            <Input data-testid="reg-phone" inputMode="tel" autoComplete="tel"
                   value={form.phone}
                   onChange={e => setForm({ ...form, phone: e.target.value })}
                   placeholder="+1 (268) ..." />
          </div>
          <div>
            <Label className="text-sm font-bold text-slate-900">Password</Label>
            <div className="relative">
              <Input type={showPw ? "text" : "password"} data-testid="reg-password"
                     autoComplete="new-password"
                     value={form.password}
                     onChange={e => setForm({ ...form, password: e.target.value })} />
              <button type="button" onClick={() => setShowPw(s => !s)} data-testid="reg-pw-toggle"
                      aria-label={showPw ? "Hide password" : "Show password"}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900 p-1">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="text-xs text-slate-500 mt-1">At least 6 characters.</div>
          </div>
          <Button type="submit" disabled={busy} data-testid="reg-submit-btn"
                  className="w-full h-11 bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold">
            {busy ? "Creating…" : "Create account"}
          </Button>
          <p className="text-sm text-slate-600 text-center">
            Have an account? <Link to="/login" className="font-bold text-[#1D4ED8] hover:underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
